import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Badge, Button, Spinner, Tabs, TabsList, TabsTrigger } from '@monksflow/monks-ui';
import { categoricalBadgeClass } from '@/shared/ui/categoricalBadge';

import { ROUTES } from '@/shared/config/routes';
import type { CategoryId } from '@/shared/constants/category-colors';
import { useAgentConfig } from '@/features/admin/model/useAgentConfig';
import { ScopeTab, WorkspaceScopeTab } from '@/features/admin/ui/ToolConfigPage';
import { KbSelectWidget, KbMultiSelectWidget } from '@/features/knowledge-bases';
import { useAuth } from '@/entities/user';
import { mergeToolUiSchemas, type MergedField } from '@/features/admin/lib/mergeToolSchemas';
import { AgentTreeView } from '@/features/admin/ui/AgentTree';
import { WorkspaceChips } from '@/features/admin/ui/WorkspaceChips';
import { getAdminToolsListDisplayName } from '@/features/admin/lib/canonicalAdminToolLabels';
import { AGENT_IDS_WITH_BASIC_INFORMATION_ON_AGENT_CONFIG_PAGE } from '@/features/admin/constants/agentBasicInformationAdmin';

import { ToolBadge } from '@/shared/ui/ToolBadge';
import { AdminPageShell } from '../AdminPageShell';
import { AgentBasicInformationSection } from './AgentBasicInformationSection';
import { OpportunityScheduleConfig } from './OpportunityScheduleConfig';
import { Trans, useTranslation } from 'react-i18next';

type TabLevel = 'organization' | 'workspace' | 'advanced';

function SubAgentNote({ fields }: { fields: MergedField[] }) {
  const { t } = useTranslation('admin');
  const multiMapped = fields.filter((f) => f.totalCount > 1);
  const partial = fields.filter((f) => f.configuredCount > 0 && f.configuredCount < f.totalCount);

  if (multiMapped.length === 0 && partial.length === 0) return null;

  return (
    <div className="mb-4 flex flex-col gap-2">
      {multiMapped.map((field) => (
        <p key={field.key} className="text-body-sm text-muted-foreground">
          <Trans
            i18nKey="admin.agentConfigPage.subAgentUsedBy"
            ns="admin"
            values={{ fieldKey: field.key, total: field.totalCount }}
            components={{ bold: <strong /> }}
          />
          {field.configuredCount > 0 && field.configuredCount < field.totalCount && (
            <>
              {' '}
              {t('admin.agentConfigPage.subAgentUsedByPartial', {
                configured: field.configuredCount,
                total: field.totalCount,
              })}
            </>
          )}
        </p>
      ))}
      {partial
        .filter((f) => f.totalCount <= 1)
        .map((field) => (
          <p key={field.key} className="text-body-sm text-warning-foreground">
            <Trans
              i18nKey="admin.agentConfigPage.subAgentConfiguredFor"
              ns="admin"
              values={{
                fieldKey: field.key,
                configured: field.configuredCount,
                total: field.totalCount,
              }}
              components={{ bold: <strong /> }}
            />
          </p>
        ))}
    </div>
  );
}

export function AgentConfigPage() {
  const { t } = useTranslation('admin');

  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabLevel>('organization');

  const workspaces = useAuth((state) => state.signedInUser.value?.workspaces) ?? [];

  const {
    agent,
    agentTree,
    toolConfigScopes,
    merged,
    orgValues,
    allHaveOrgConfig,
    fullyConfiguredWorkspaceIds,
    workspaceConfigs,
    isLoading,
    save,
    isSaving,
    remove,
    isRemoving,
  } = useAgentConfig({ agentId: agentId! });

  const fullyConfiguredWorkspaces = workspaces.filter((ws) =>
    fullyConfiguredWorkspaceIds.includes(ws.id),
  );

  const mergedUiSchema = merged ? mergeToolUiSchemas(merged.fields) : undefined;

  const tag = (agent?.tag ?? 'deliver') satisfies CategoryId;

  const handleOrgSave = useCallback(
    async (data: Record<string, unknown>) => {
      await save({ formData: data });
    },
    [save],
  );

  const handleWorkspaceSave = useCallback(
    async (workspaceId: string, data: Record<string, unknown>) => {
      await save({ formData: data, workspaceId });
    },
    [save],
  );

  const handleOrgDelete = useCallback(async () => {
    await remove({});
  }, [remove]);

  const handleWorkspaceRemove = useCallback(
    async (workspaceId: string) => {
      await remove({ workspaceId });
    },
    [remove],
  );

  const handleWorkspaceBulkSave = useCallback(
    async (workspaceIds: string[], data: Record<string, unknown>) => {
      await Promise.all(workspaceIds.map((wsId) => save({ formData: data, workspaceId: wsId })));
    },
    [save],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <p className="text-body-lg text-muted-foreground">
          {t('admin.agentConfigPage.agentNotFound')}
        </p>
        <Button type="button" variant="outline" onClick={() => void navigate(ROUTES.ADMIN_TOOLS)}>
          {t('admin.agentConfigPage.backToTools')}
        </Button>
      </div>
    );
  }

  const title = getAdminToolsListDisplayName(agent.id, agent.name);

  // `ownsBodyInset`: the padded scroll region below is this page's inset in both frames,
  // and it is exactly the one the shell would otherwise add.
  return (
    <AdminPageShell
      title={title}
      titleIcon={<ToolBadge toolId={agent.id} category={agent.tag} size="xs" />}
      parents={[{ label: t('admin.adminLayout.tools'), href: ROUTES.ADMIN_TOOLS }]}
      ownsBodyInset
    >
      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <div className="flex flex-col gap-6">
          {AGENT_IDS_WITH_BASIC_INFORMATION_ON_AGENT_CONFIG_PAGE.has(agent.id) && (
            <AgentBasicInformationSection agentId={agent.id} />
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabLevel)}>
            <TabsList className="h-auto justify-start">
              {/* eslint-disable-next-line i18next/no-literal-string -- tab state value compared against activeTab */}
              <TabsTrigger value="organization">
                {t('admin.agentConfigPage.organization')}
                {allHaveOrgConfig && (
                  <Badge variant="positive" roundness="full">
                    {t('admin.agentConfigPage.configured')}
                  </Badge>
                )}
                {!allHaveOrgConfig && orgValues && (
                  <Badge roundness="full" className={categoricalBadgeClass('orange')}>
                    {t('admin.agentConfigPage.partiallyConfigured')}
                  </Badge>
                )}
              </TabsTrigger>
              {/* eslint-disable-next-line i18next/no-literal-string -- tab state value compared against activeTab */}
              <TabsTrigger value="workspace">
                {t('admin.agentConfigPage.workspace')}
                {fullyConfiguredWorkspaces.length > 0 && (
                  <WorkspaceChips workspaces={fullyConfiguredWorkspaces} color="green" />
                )}
                {fullyConfiguredWorkspaces.length === 0 && workspaceConfigs.length > 0 && (
                  <Badge roundness="full" className={categoricalBadgeClass('orange')}>
                    {t('admin.agentConfigPage.partiallyConfiguredCount', {
                      workspaceCount: workspaceConfigs.length,
                    })}
                  </Badge>
                )}
              </TabsTrigger>
              {/* eslint-disable-next-line i18next/no-literal-string -- tab state value compared against activeTab */}
              <TabsTrigger value="advanced">{t('admin.agentConfigPage.advanced')}</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Organization tab */}
          {activeTab === 'organization' && merged && (
            <>
              <SubAgentNote fields={merged.fields} />
              <ScopeTab
                toolId={agentId!}
                jsonSchema={merged.jsonSchema}
                existingConfig={orgValues ? { data: orgValues } : null}
                description={t('admin.agentConfigPage.configurationForThisToolAcross')}
                onSave={handleOrgSave}
                onDelete={handleOrgDelete}
                isSaving={isSaving}
                isRemoving={isRemoving}
                uiSchemaOverride={mergedUiSchema}
                kbSelectWidget={KbSelectWidget}
                kbMultiSelectWidget={KbMultiSelectWidget}
              />

              {/* Show opportunity schedule for deep-research-opportunity-identifier */}
              {agentId === 'deep-research-opportunity-identifier' && (
                <OpportunityScheduleConfig
                  fullyConfiguredWorkspaceIds={fullyConfiguredWorkspaceIds}
                />
              )}
            </>
          )}

          {activeTab === 'organization' && !merged && (
            <p className="text-body-md text-muted-foreground">
              {t('admin.agentConfigPage.thisToolHasNoConfigurable')}
            </p>
          )}

          {/* Workspace tab */}
          {activeTab === 'workspace' && merged && (
            <WorkspaceScopeTab
              toolId={agentId!}
              jsonSchema={merged.jsonSchema}
              configs={workspaceConfigs}
              allWorkspaces={workspaces}
              fullyConfiguredWorkspaceIds={fullyConfiguredWorkspaceIds}
              description={t('admin.agentConfigPage.workspaceSpecificOverridesForThis')}
              fallbackLabel={t('admin.agentConfigPage.allWorkspacesWillUseThe')}
              onSave={handleWorkspaceSave}
              onRemove={handleWorkspaceRemove}
              onBulkSave={handleWorkspaceBulkSave}
              isSaving={isSaving}
              isRemoving={isRemoving}
              isBulkSaving={isSaving}
              uiSchemaOverride={mergedUiSchema}
              kbSelectWidget={KbSelectWidget}
              kbMultiSelectWidget={KbMultiSelectWidget}
            />
          )}

          {/* Advanced tab — shows agent tree for per-tool overrides */}
          {activeTab === 'advanced' && (
            <div className="flex flex-col gap-4">
              <p className="text-body-md text-muted-foreground">
                {t('admin.agentConfigPage.configureIndividualToolsForSpecific')}
              </p>
              {agentTree && (
                <AgentTreeView
                  node={agentTree}
                  toolConfigScopes={toolConfigScopes}
                  allWorkspaces={workspaces}
                  onConfigure={(toolId, ownerAgentId) =>
                    void navigate(
                      `${ROUTES.ADMIN_TOOL_CONFIG(agentId!, toolId)}?ownerAgentId=${ownerAgentId}`,
                    )
                  }
                  tag={tag}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </AdminPageShell>
  );
}
